import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  StatusBadge,
} from '@/components/ui'
import { Linkedin, Plus, Trash2, UserPlus, Link2Off, ShieldCheck, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils.ts'

// ─── Types ───────────────────────────────────────────────────────────────────

type ProfileStatus = 'pending' | 'connected' | 'expired' | 'error'

interface LinkedInProfile {
  id: string
  userId: number | null
  tenantId: number
  profileName: string
  linkedinId: string | null
  firstName: string | null
  lastName: string | null
  email: string | null
  avatarUrl: string | null
  tokenExpiresAt: string | null
  status: ProfileStatus
  lastError: string | null
  createdAt: string
}

interface AccessEntry {
  id: number
  profileId: string
  userId: number
  canPost: boolean
  grantedAt: string
}

// ─── Status badge mapping ─────────────────────────────────────────────────────

const STATUS_LABEL: Record<ProfileStatus, string> = {
  pending: 'Ausstehend',
  connected: 'Verbunden',
  expired: 'Abgelaufen',
  error: 'Fehler',
}

// Map to StatusBadge status values used in the design system
const STATUS_BADGE: Record<ProfileStatus, string> = {
  pending: 'pending',
  connected: 'active',
  expired: 'expired',
  error: 'error',
}

// ─── API helpers ──────────────────────────────────────────────────────────────

const api = {
  listProfiles: (): Promise<LinkedInProfile[]> =>
    apiClient.get('/api/linkedin/profiles').then((r) => r.data),
  createProfile: (profileName: string): Promise<LinkedInProfile> =>
    apiClient.post('/api/linkedin/profiles', { profileName }).then((r) => r.data),
  deleteProfile: (id: string): Promise<void> =>
    apiClient.delete(`/api/linkedin/profiles/${id}`).then(() => undefined),
  listAccess: (id: string): Promise<AccessEntry[]> =>
    apiClient.get(`/api/linkedin/profiles/${id}/access`).then((r) => r.data),
  grantAccess: (id: string, userId: number, canPost: boolean): Promise<void> =>
    apiClient.post(`/api/linkedin/profiles/${id}/access`, { userId, canPost }).then(() => undefined),
  revokeAccess: (id: string, userId: number): Promise<void> =>
    apiClient.delete(`/api/linkedin/profiles/${id}/access/${userId}`).then(() => undefined),
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function LinkedInProfilesPage() {
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  // Feedback from OAuth redirect
  const connectedProfileId = searchParams.get('connected')
  const oauthError = searchParams.get('error')

  const [createOpen, setCreateOpen] = useState(false)
  const [newProfileName, setNewProfileName] = useState('')
  const [accessModalProfileId, setAccessModalProfileId] = useState<string | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  const {
    data: profiles,
    isLoading,
    error,
  } = useQuery({ queryKey: ['linkedin-profiles'], queryFn: api.listProfiles })

  const createMutation = useMutation({
    mutationFn: () => api.createProfile(newProfileName.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['linkedin-profiles'] })
      setCreateOpen(false)
      setNewProfileName('')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteProfile(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['linkedin-profiles'] })
      setDeleteConfirmId(null)
    },
  })

  if (isLoading) return <LoadingState label="LinkedIn-Profile werden geladen…" />
  if (error) {
    return (
      <ErrorState
        title="Fehler beim Laden"
        description="LinkedIn-Profile konnten nicht geladen werden."
        details={String(error)}
      />
    )
  }

  const profileForAccess = profiles?.find((p) => p.id === accessModalProfileId) ?? null
  const profileForDelete = profiles?.find((p) => p.id === deleteConfirmId) ?? null

  return (
    <div className="space-y-6">
      <PageHeader
        title="LinkedIn Profile"
        breadcrumbs={[
          { label: 'Neukundengewinnung', to: '/neukundengewinnung' },
          { label: 'LinkedIn Profile' },
        ]}
        actions={
          <Button
            variant="primary"
            size="sm"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Neues Profil
          </Button>
        }
      />

      {/* OAuth Feedback Banner */}
      {connectedProfileId && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">
          <ShieldCheck className="w-5 h-5 flex-shrink-0" />
          LinkedIn-Profil erfolgreich verbunden.
        </div>
      )}
      {oauthError && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          LinkedIn-Verbindung fehlgeschlagen: <span className="font-mono">{oauthError}</span>
        </div>
      )}

      {/* Config hint when no profiles exist yet */}
      {profiles?.length === 0 && (
        <EmptyState
          icon={<Linkedin className="w-12 h-12 text-blue-600" />}
          title="Noch keine LinkedIn-Profile"
          description='Legen Sie ein neues Profil an und verbinden Sie es per OAuth mit Ihrem LinkedIn-Konto. Voraussetzung: LinkedIn Developer App (Client-ID & Secret) muss unter Einstellungen → Adapter → LinkedIn konfiguriert sein.'
          action={
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              <Plus className="w-4 h-4 mr-1.5" />
              Profil anlegen
            </Button>
          }
        />
      )}

      {/* Profile table */}
      {profiles && profiles.length > 0 && (
        <Card>
          <Card.Header>
            <h2 className="text-h3">Verbundene Profile</h2>
          </Card.Header>
          <Card.Body className="p-0">
            <DataTable
              data={profiles}
              columns={[
                {
                  id: 'avatar',
                  header: '',
                  size: 48,
                  cell: ({ row }) => {
                    const p = row.original
                    return p.avatarUrl ? (
                      <img
                        src={p.avatarUrl}
                        alt={p.profileName}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <Linkedin className="w-4 h-4 text-blue-600" />
                      </div>
                    )
                  },
                },
                {
                  id: 'profileName',
                  header: 'Name',
                  accessorKey: 'profileName',
                  cell: ({ row }) => {
                    const p = row.original
                    const displayName =
                      p.firstName && p.lastName ? `${p.firstName} ${p.lastName}` : null
                    return (
                      <div>
                        <p className="font-medium text-sm">{p.profileName}</p>
                        {displayName && (
                          <p className="text-xs text-gray-500">{displayName}</p>
                        )}
                        {p.email && (
                          <p className="text-xs text-gray-400">{p.email}</p>
                        )}
                      </div>
                    )
                  },
                },
                {
                  id: 'status',
                  header: 'Status',
                  accessorKey: 'status',
                  cell: ({ row }) => (
                    <StatusBadge status={STATUS_BADGE[row.original.status]} label={STATUS_LABEL[row.original.status]} />
                  ),
                },
                {
                  id: 'tokenExpiry',
                  header: 'Token läuft ab',
                  accessorKey: 'tokenExpiresAt',
                  cell: ({ row }) => {
                    const exp = row.original.tokenExpiresAt
                    if (!exp) return <span className="text-gray-400 text-sm">–</span>
                    const d = new Date(exp)
                    const isNear = d.getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000
                    return (
                      <span className={cn('text-sm', isNear ? 'text-amber-600 font-medium' : 'text-gray-600')}>
                        {d.toLocaleDateString('de-DE')}
                      </span>
                    )
                  },
                },
                {
                  id: 'actions',
                  header: '',
                  size: 160,
                  cell: ({ row }) => {
                    const p = row.original
                    return (
                      <div className="flex items-center gap-2 justify-end">
                        {/* Re-connect / Connect button */}
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => {
                            window.location.href = `/api/linkedin/oauth/start?profileId=${p.id}`
                          }}
                          title={p.status === 'connected' ? 'Neu verbinden' : 'Mit LinkedIn verbinden'}
                        >
                          {p.status === 'connected' ? (
                            <><Link2Off className="w-3.5 h-3.5 mr-1" />Neu verbinden</>
                          ) : (
                            <><Linkedin className="w-3.5 h-3.5 mr-1" />Verbinden</>
                          )}
                        </Button>
                        {/* Mitpfleger */}
                        <IconButton
                          icon={<UserPlus className="w-3.5 h-3.5" />}
                          aria-label="Mitpfleger verwalten"
                          size="xs"
                          variant="ghost"
                          onClick={() => setAccessModalProfileId(p.id)}
                        />
                        {/* Delete */}
                        <IconButton
                          icon={<Trash2 className="w-3.5 h-3.5" />}
                          aria-label="Profil löschen"
                          size="xs"
                          variant="ghost"
                          onClick={() => setDeleteConfirmId(p.id)}
                        />
                      </div>
                    )
                  },
                },
              ]}
            />
          </Card.Body>
        </Card>
      )}

      {/* Create Profile Modal */}
      <Modal
        open={createOpen}
        onClose={() => { setCreateOpen(false); setNewProfileName('') }}
        title="Neues LinkedIn-Profil anlegen"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Geben Sie einen internen Namen für dieses LinkedIn-Konto an. Anschließend können Sie es per OAuth mit LinkedIn verbinden.
          </p>
          <Input
            label="Profilname (intern)"
            placeholder="z.B. Max Mustermann – Vertrieb"
            value={newProfileName}
            onChange={(e) => setNewProfileName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newProfileName.trim()) createMutation.mutate()
            }}
          />
          {createMutation.error && (
            <p className="text-sm text-red-600">{String(createMutation.error)}</p>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="secondary" onClick={() => { setCreateOpen(false); setNewProfileName('') }}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={() => createMutation.mutate()}
            disabled={!newProfileName.trim() || createMutation.isPending}
          >
            {createMutation.isPending ? 'Anlegen…' : 'Anlegen'}
          </Button>
        </div>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        open={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        title="Profil löschen"
        size="sm"
      >
        <p className="text-sm text-gray-700">
          Möchten Sie das Profil <strong>{profileForDelete?.profileName}</strong> und alle gespeicherten Tokens unwiderruflich löschen?
        </p>
        {deleteMutation.error && (
          <p className="text-sm text-red-600 mt-2">{String(deleteMutation.error)}</p>
        )}
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="secondary" onClick={() => setDeleteConfirmId(null)}>
            Abbrechen
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteConfirmId && deleteMutation.mutate(deleteConfirmId)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Löschen…' : 'Löschen'}
          </Button>
        </div>
      </Modal>

      {/* Access / Mitpfleger Modal */}
      {profileForAccess && (
        <AccessModal
          profile={profileForAccess}
          onClose={() => setAccessModalProfileId(null)}
        />
      )}
    </div>
  )
}

// ─── Access Modal ─────────────────────────────────────────────────────────────

function AccessModal({
  profile,
  onClose,
}: {
  profile: LinkedInProfile
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [newUserId, setNewUserId] = useState('')
  const [canPost, setCanPost] = useState(false)

  const { data: access, isLoading } = useQuery({
    queryKey: ['linkedin-access', profile.id],
    queryFn: () => api.listAccess(profile.id),
  })

  const grantMutation = useMutation({
    mutationFn: () => api.grantAccess(profile.id, Number(newUserId), canPost),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['linkedin-access', profile.id] })
      setNewUserId('')
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (userId: number) => api.revokeAccess(profile.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['linkedin-access', profile.id] })
    },
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={`Mitpfleger — ${profile.profileName}`}
      size="md"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Mitpfleger können dieses LinkedIn-Profil einsehen. Bei aktivierter Option „Posten erlaubt" dürfen sie auch Beiträge verfassen.
        </p>

        {isLoading && <LoadingState label="Lade Zugriffsberechtigungen…" />}

        {access && access.length === 0 && (
          <p className="text-sm text-gray-500 italic">Noch keine Mitpfleger.</p>
        )}

        {access && access.length > 0 && (
          <ul className="divide-y divide-gray-100 border rounded-lg">
            {access.map((a) => (
              <li key={a.id} className="flex items-center justify-between px-3 py-2.5 text-sm">
                <div>
                  <span className="font-medium">User #{a.userId}</span>
                  {a.canPost && (
                    <span className="ml-2 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                      Posten erlaubt
                    </span>
                  )}
                </div>
                <IconButton
                  icon={<Trash2 className="w-3.5 h-3.5" />}
                  aria-label="Zugriff entfernen"
                  size="xs"
                  variant="ghost"
                  onClick={() => revokeMutation.mutate(a.userId)}
                />
              </li>
            ))}
          </ul>
        )}

        <div className="border-t pt-4 space-y-3">
          <p className="text-xs font-medium text-gray-700 uppercase tracking-wide">Mitpfleger hinzufügen</p>
          <div className="flex items-end gap-2">
            <Input
              label="User-ID"
              type="number"
              placeholder="z.B. 42"
              value={newUserId}
              onChange={(e) => setNewUserId(e.target.value)}
              className="flex-1"
            />
            <div className="flex items-center gap-1.5 pb-1">
              <input
                type="checkbox"
                id="canPost"
                checked={canPost}
                onChange={(e) => setCanPost(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="canPost" className="text-sm text-gray-600">Posten</label>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => grantMutation.mutate()}
              disabled={!newUserId || grantMutation.isPending}
            >
              Hinzufügen
            </Button>
          </div>
          {grantMutation.error && (
            <p className="text-sm text-red-600">{String(grantMutation.error)}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <Button variant="secondary" onClick={onClose}>Schließen</Button>
      </div>
    </Modal>
  )
}
